import React, { useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { Form, Input, Select, Row, Col, Button, Table, Spin } from 'antd';
import AppModal from '../../../../layout/popup';
import { useSelector } from 'react-redux';
import { CloseOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import styles from '../MemberDataBase.module.css';

const { Option } = Select;
const vehicleTypes = ['รถยนต์', 'รถมอเตอร์ไซค์'];

function useDebouncedFn() {
  const timersRef = useRef(new Map());
  const run = useCallback((key, fn, delay = 400) => {
    const timers = timersRef.current;
    if (timers.has(key)) {
      clearTimeout(timers.get(key));
    }
    const id = setTimeout(() => {
      timers.delete(key);
      fn();
    }, delay);
    timers.set(key, id);
  }, []);
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
    };
  }, []);
  return run;
}

const normalizeObject = (obj) =>
  JSON.parse(
    JSON.stringify(obj, (k, v) => {
      if (typeof v === 'string') return v.trim();
      return v;
    })
  );

const EditMemberModal = ({
  open,
  onCancel,
  onOk,
  confirmLoading,
  form,
  record,
}) => {
  const [socket, setSocket] = useState(null);
  const [vehicleCheckingMap, setVehicleCheckingMap] = useState({});
  const [vehicleCheckStatusMap, setVehicleCheckStatusMap] = useState({});
  const [houseNumberChecking, setHouseNumberChecking] = useState(false);
  const [memberCheckStatus, setMemberCheckStatus] = useState(null);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  const user = useSelector((state) => state.user);
  const memberId = record?.id ?? null;
  const debounceRun = useDebouncedFn();

  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_SOCKETIO);
    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onVehicle = (result) => {
      const key = result?.fieldKey;
      const isDuplicate = !!(result?.isDuplicate ?? result?.result);
      if (key == null) return;
      setVehicleCheckStatusMap((prev) => ({ ...prev, [key]: isDuplicate ? 'error' : 'success' }));
      setVehicleCheckingMap((prev) => ({ ...prev, [key]: false }));
    };

    const onHouse = (result) => {
      const isDuplicate = !!(result?.isDuplicate ?? result);
      setMemberCheckStatus(isDuplicate ? 'error' : 'success');
      setHouseNumberChecking(false);
    };

    socket.on('getVehicleNumber', onVehicle);
    socket.on('getHouseNumber', onHouse);

    return () => {
      socket.off('getVehicleNumber', onVehicle);
      socket.off('getHouseNumber', onHouse);
    };
  }, [socket]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setIsDataChanged(false);
      setMemberCheckStatus(null);
      setHouseNumberChecking(false);
      setVehicleCheckingMap({});
      setVehicleCheckStatusMap({});
      return;
    }
    if (!record) return;

    const vehicles = Array.isArray(record.vehicles) ? record.vehicles : [];
    const formValues = {
      houseNumber: record.houseNumber ?? '',
      status: record.status ?? undefined,
      vehicles: vehicles.map((v) => ({
        id: v.id, // เก็บ id ไว้เพื่อให้อัปเดตได้ถูกต้อง
        vehicleType: v.vehicleType ?? v.type ?? undefined,
        licensePlate: v.licensePlate ?? '',
        province: v.province ?? '',
      })),
    };

    form.setFieldsValue(formValues);
    setInitialValues(normalizeObject(formValues));
    setIsDataChanged(false);
    setMemberCheckStatus(null);
    setHouseNumberChecking(false);
    setVehicleCheckingMap({});
    setVehicleCheckStatusMap({});
  }, [open, record, form]);

  const handleValuesChange = useCallback(
    (_changed, allValues) => {
      const isChanged =
        JSON.stringify(normalizeObject(allValues)) !== JSON.stringify(initialValues);
      setIsDataChanged(isChanged);
    },
    [initialValues]
  );

  const onHouseNumberChange = useCallback(
    (e) => {
      const houseNumberRaw = e.target.value ?? '';
      const houseNumber = houseNumberRaw.trim();

      setHouseNumberChecking(!!houseNumber);
      setMemberCheckStatus(null);

      if (!houseNumber) {
        setHouseNumberChecking(false);
        return;
      }

      const data = { villageId: user?.id, houseNumber, memberId };

      if (socket) {
        debounceRun('__house__', () => {
          socket.emit('checkHouseNumber', data);
        }, 400);
      } else {
        setHouseNumberChecking(false);
      }
    },
    [socket, debounceRun, user?.id, memberId]
  );

  const createOnVehicleNumberChange = useCallback(
    (fieldKey) => (e) => {
      const plateRaw = e.target.value ?? '';
      const licensePlate = plateRaw.trim();

      setVehicleCheckStatusMap((prev) => ({ ...prev, [fieldKey]: null }));
      setVehicleCheckingMap((prev) => ({ ...prev, [fieldKey]: !!licensePlate }));

      if (!licensePlate) return;

      const payload = {
        fieldKey,
        vehicleNumber: licensePlate,
        memberId,
      };

      if (socket) {
        debounceRun(`veh_${fieldKey}`, () => {
          socket.emit('checkVehicleNumber', payload);
        }, 400);
      } else {
        setVehicleCheckingMap((prev) => ({ ...prev, [fieldKey]: false }));
      }
    },
    [socket, debounceRun, memberId]
  );

  const hasFormErrors = Form.useWatch([], form) && form
    ? form.getFieldsError().some(({ errors }) => errors.length)
    : false;

  const okDisabled = !isDataChanged || hasFormErrors || houseNumberChecking ||
    Object.values(vehicleCheckingMap).some(Boolean);

  return (
    <AppModal
      icon={<EditOutlined />}
      title="แก้ไขข้อมูลสมาชิก"
      subtitle="แก้ไขข้อมูลบ้านเลขที่และรายการรถ"
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={760}
      okButtonProps={{ disabled: okDisabled }}
    >
      <Form
        form={form}
        layout="vertical"
        className={styles.editForm}
        preserve={false}
        onValuesChange={handleValuesChange}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="houseNumber"
              label="บ้านเลขที่"
              hasFeedback
              validateStatus={memberCheckStatus}
              help={memberCheckStatus === 'error' ? 'โปรดกรอกเลขที่บ้านที่ไม่ซ้ำกับในระบบ' : ''}
              rules={[{ required: true, message: 'กรุณากรอกบ้านเลขที่!' }]}
            >
              <Input
                placeholder="กรอกบ้านเลขที่"
                onChange={onHouseNumberChange}
                suffix={houseNumberChecking && <Spin size="small" />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="status"
              label="สถานะ"
              rules={[{ required: true, message: 'กรุณาเลือกสถานะ!' }]}
            >
              <Select placeholder="เลือกสถานะ">
                <Option value="เจ้าของ">เจ้าของ</Option>
                <Option value="คนเช่า">คนเช่า</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.List name="vehicles">
          {(fields, { add, remove }) => (
            <>
              <Table
                dataSource={fields.map((field) => ({ ...field, key: field.key }))}
                columns={[
                  {
                    title: 'ลำดับ',
                    key: 'index',
                    render: (_text, _rec, index) => index + 1,
                    width: 100,
                    align: 'center',
                  },
                  {
                    title: 'ประเภทรถ',
                    key: 'vehicleType',
                    render: (_text, rec) => (
                      <Form.Item
                        name={[rec.name, 'vehicleType']}
                        rules={[{ required: true, message: 'กรุณาเลือกประเภทรถ!' }]}
                        noStyle
                      >
                        <Select placeholder="เลือกประเภทรถ" style={{ width: '100%' }}>
                          {vehicleTypes.map((type) => (
                            <Option key={type} value={type}>
                              {type}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ),
                    width: 200,
                  },
                  {
                    title: 'หมายเลขป้ายทะเบียนรถ',
                    key: 'licensePlate',
                    render: (_text, rec) => (
                      <Form.Item
                        name={[rec.name, 'licensePlate']}
                        rules={[{ required: true, message: 'กรุณากรอกป้ายทะเบียน!' }]}
                        hasFeedback
                        validateStatus={vehicleCheckStatusMap[rec.key]}
                        help={
                          vehicleCheckStatusMap[rec.key] === 'error'
                            ? 'ป้ายทะเบียนซ้ำในระบบ'
                            : ''
                        }
                        noStyle
                      >
                        <Input
                          placeholder="ป้ายทะเบียน"
                          onChange={createOnVehicleNumberChange(rec.key)}
                          suffix={vehicleCheckingMap[rec.key] && <Spin size="small" />}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    ),
                    width: 250,
                  },
                  {
                    title: 'จังหวัด',
                    key: 'province',
                    render: (_text, rec) => (
                      <Form.Item
                        name={[rec.name, 'province']}
                        rules={[{ required: true, message: 'กรุณากรอกจังหวัด!' }]}
                        noStyle
                      >
                        <Input placeholder="จังหวัด" style={{ width: '100%' }} />
                      </Form.Item>
                    ),
                    width: 200,
                  },
                  {
                    title: 'จัดการ',
                    key: 'action',
                    render: (_text, rec) => (
                      <Button
                        type="link"
                        danger
                        onClick={() => remove(rec.name)}
                        icon={<CloseOutlined />}
                      >
                        ลบ
                      </Button>
                    ),
                    width: 100,
                    align: 'center',
                  },
                ]}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 600 }}
              />

              <Button
                type="dashed"
                onClick={() => add()}
                icon={<PlusOutlined />}
                className={styles.addVehicleBtn}
                style={{ marginTop: 16 }}
              >
                เพิ่มข้อมูลรถ
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </AppModal>
  );
};

export default EditMemberModal;
